/**
 * @file     mortgageInsurance.ts
 * @purpose  CMHC mortgage-insurance premium rates and the insured-amortization
 *           ceiling, for mortgages where the down payment is under 20%.
 */
import { isInsuredMortgagePriceEligible, minimumDownPayment } from './downPayment';

/**
 * Premium as a share of the loan amount, by loan-to-value band. These are the
 * published CMHC rates for owner-occupied purchases with traditional down
 * payment sources.
 */
export const CMHC_PREMIUM_BY_LTV: ReadonlyArray<{ maxLtv: number; rate: number }> = [
  { maxLtv: 0.65, rate: 0.006 },
  { maxLtv: 0.75, rate: 0.017 },
  { maxLtv: 0.8, rate: 0.024 },
  { maxLtv: 0.85, rate: 0.028 },
  { maxLtv: 0.9, rate: 0.031 },
  { maxLtv: 0.95, rate: 0.04 },
];

/** Premium rate for a loan-to-value; 0 above 95%, where no insurer writes the loan. */
export function cmhcPremiumRate(ltv: number): number {
  return CMHC_PREMIUM_BY_LTV.find((b) => ltv <= b.maxLtv)?.rate ?? 0;
}

/** Insured mortgages amortize over 25 years, with a 30-year first-time-buyer exception. */
export const INSURED_AMORTIZATION_MAX_YEARS = 25;
export const INSURED_AMORTIZATION_MAX_YEARS_FIRST_TIME_NEW_BUILD = 30;

export interface InsuranceResult {
  /** True when the down payment is under 20% and the price qualifies for insurance. */
  required: boolean;
  /** Premium in dollars, added to the loan balance rather than paid up front. */
  premium: number;
  /** The premium rate applied, as a share of the base loan. */
  premiumRate: number;
  /** Loan-to-value before the premium is added. */
  loanToValue: number;
}

/**
 * A down payment under 20% requires insurance, but only below the insured price
 * cap. At or above the cap no insurer will write the policy, which is why
 * `minimumDownPayment` already demands 20% there.
 */
export function mortgageInsurance(price: number, downPayment: number): InsuranceResult {
  const none = { required: false, premium: 0, premiumRate: 0, loanToValue: 0 };
  if (!Number.isFinite(price) || price <= 0) return none;
  if (!Number.isFinite(downPayment) || downPayment < 0) return none;

  const base = price - downPayment;
  if (base <= 0) return none;

  const loanToValue = base / price;
  if (loanToValue <= 0.8) return { ...none, loanToValue };
  if (!isInsuredMortgagePriceEligible(price)) return { ...none, loanToValue };

  const premiumRate = cmhcPremiumRate(loanToValue);
  if (premiumRate === 0) return { ...none, loanToValue };

  return {
    required: true,
    premium: Math.round(base * premiumRate),
    premiumRate,
    loanToValue,
  };
}

/** The financed principal: purchase price less down payment, plus any premium. */
export function financedPrincipal(price: number, downPayment: number): number {
  const base = Math.max(0, price - downPayment);
  return base + mortgageInsurance(price, downPayment).premium;
}

/** True when the down payment clears the legal minimum for the price. */
export function meetsMinimumDownPayment(price: number, downPayment: number): boolean {
  return downPayment >= minimumDownPayment(price);
}
